<div class="min-h-full bg-[#fafaf7] font-sans">

  <!-- LOADING -->
  @if (isLoading()) {
    <div class="flex items-center justify-center min-h-full">
      <div class="flex flex-col items-center gap-5">
        <div class="relative w-12 h-12">
          <div class="absolute inset-0 rounded-full border-4 border-stone-200"></div>
          <div class="absolute inset-0 rounded-full border-4 border-t-primary-400 animate-spin"></div>
        </div>
        <div class="text-center">
          <p class="text-stone-600 font-medium text-sm">Chargement&hellip;</p>
        </div>
      </div>
    </div>
  }

  <!-- DEMANDES LIST -->
  @if (!isLoading()) {
    <div class="animate-fade-in">
      <div class="max-w-[1200px] mx-auto px-5 sm:px-8 py-8 space-y-8">

        <!-- Stats cards -->
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div class="bg-white rounded-2xl border border-stone-100 shadow-sm p-4 flex flex-col gap-1">
            <span class="text-xs font-medium text-stone-500 uppercase tracking-wider">En attente</span>
            <span class="text-2xl font-bold text-amber-600">{{ stats().total_pending || 0 }}</span>
          </div>
          <div class="bg-white rounded-2xl border border-stone-100 shadow-sm p-4 flex flex-col gap-1">
            <span class="text-xs font-medium text-stone-500 uppercase tracking-wider">Valid&eacute;es</span>
            <span class="text-2xl font-bold text-green-600">{{ stats().total_validated || 0 }}</span>
          </div>
          <div class="bg-white rounded-2xl border border-stone-100 shadow-sm p-4 flex flex-col gap-1">
            <span class="text-xs font-medium text-stone-500 uppercase tracking-wider">Rejet&eacute;es</span>
            <span class="text-2xl font-bold text-red-600">{{ stats().total_rejected || 0 }}</span>
          </div>
          <div class="bg-white rounded-2xl border border-stone-100 shadow-sm p-4 flex flex-col gap-1">
            <span class="text-xs font-medium text-stone-500 uppercase tracking-wider">Annul&eacute;es</span>
            <span class="text-2xl font-bold text-stone-500">{{ stats().total_cancelled || 0 }}</span>
          </div>
        </div>

        <!-- Entity type tabs -->
        <div class="flex items-center gap-1 bg-white rounded-2xl border border-stone-100 shadow-sm p-1.5">
          @for (et of entityTypes; track et.value) {
            <button
              type="button"
              class="px-4 py-2 rounded-xl text-xs font-semibold transition-all duration-200"
              [class.bg-primary-600]="entityType() === et.value"
              [class.text-white]="entityType() === et.value"
              [class.text-stone-600]="entityType() !== et.value"
              [class.hover:bg-stone-50]="entityType() !== et.value"
              (click)="onEntityTypeChange(et.value)"
            >
              {{ et.label }}
            </button>
          }
        </div>

        <!-- Demandes table -->
        <div class="bg-white rounded-3xl shadow-sm border border-stone-100">

          <!-- Header - Search and Filters -->
          <div class="px-4 sm:px-6 py-4 border-b border-stone-100 flex items-center gap-3 sm:gap-4 flex-wrap">

            <!-- Search bar -->
            <div class="relative flex-1 min-w-[200px]">
              <svg class="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <input
                type="search"
                class="w-full bg-white border border-stone-200 text-stone-800 placeholder-stone-400 text-sm rounded-lg pl-11 pr-4 py-2 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                placeholder="Rechercher par nom, pr&eacute;nom ou CIN&hellip;"
                [value]="searchQuery()"
                (input)="onSearch($any($event.target).value)"
                aria-label="Rechercher une demande"
              />
              @if (searchQuery()) {
                <span class="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-stone-400 bg-stone-50 px-2 py-0.5 rounded">
                  {{ pagination().total }} r&eacute;sultat(s)
                </span>
              }
            </div>

            <!-- Status filter + count -->
            <div class="list-filters">
              <select
                class="bg-white border border-stone-200 text-stone-800 text-xs rounded-lg px-3 py-2 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                [value]="statusFilter()"
                (change)="onStatusFilterChange($any($event.target).value)"
              >
                @for (opt of statusOptions; track opt.value) {
                  <option [value]="opt.value">{{ opt.label }}</option>
                }
              </select>
              <span class="text-xs text-stone-400">
                {{ pagination().total }} demande@if (pagination().total > 1) {s}
              </span>
            </div>
          </div>

          <div class="overflow-x-auto">
            <table class="w-full min-w-[700px] text-sm" role="table" aria-label="Liste des demandes">
              <thead>
                <tr class="border-b border-stone-100 bg-stone-50">
                  <th class="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">Type</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">Nom</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">Pr&eacute;nom</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">CIN</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">Club</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">P&eacute;riode</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">Statut</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">Date</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-stone-50">
                @for (demande of demandes(); track demande.demande_id) {
                  <tr class="hover:bg-stone-50 transition-colors duration-150 group">

                    <!-- Type -->
                    <td class="px-4 py-3">
                      <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-primary-50 text-primary-600 border border-primary-100 uppercase tracking-wider">
                        {{ demande.demande_type || '&mdash;' }}
                      </span>
                    </td>

                    <!-- Nom -->
                    <td class="px-4 py-3">
                      <span class="text-xs font-semibold text-stone-800">{{ demande.data_last_name || '&mdash;' }}</span>
                    </td>

                    <!-- Pr&eacute;nom -->
                    <td class="px-4 py-3">
                      <span class="text-xs text-stone-500">{{ demande.data_first_name || '&mdash;' }}</span>
                    </td>

                    <!-- CIN -->
                    <td class="px-4 py-3">
                      <span class="text-xs text-stone-600 font-mono">{{ demande.data_identity_number || '&mdash;' }}</span>
                    </td>

                    <!-- Club -->
                    <td class="px-4 py-3">
                      <span class="text-xs text-stone-600">{{ demande.club_name || '&mdash;' }}</span>
                    </td>

                    <!-- P&eacute;riode -->
                    <td class="px-4 py-3">
                      <span class="text-xs text-stone-600">{{ demande.licenceperiod_name || '&mdash;' }}</span>
                    </td>

                    <!-- Statut -->
                    <td class="px-4 py-3">
                      <span
                        class="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full"
                        [style.color]="getStatusClass(demande.demande_status).text"
                        [style.backgroundColor]="getStatusClass(demande.demande_status).bg"
                      >
                        <span class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                          [style.backgroundColor]="getStatusClass(demande.demande_status).dot">
                        </span>
                        {{ getStatusLabel(demande.demande_status) }}
                      </span>
                    </td>

                    <!-- Date -->
                    <td class="px-4 py-3">
                      <span class="text-xs text-stone-500">{{ demande.created_at | date:'dd/MM/yyyy' }}</span>
                    </td>

                    <!-- Actions -->
                    <td class="px-4 py-3 text-right flex items-center justify-end gap-2">
                      <button
                        type="button"
                        class="w-8 h-8 rounded-xl flex items-center justify-center text-stone-400 hover:bg-primary-50 hover:text-primary-600 transition-all duration-200 cursor-pointer"
                        (click)="viewDetail(demande)"
                        aria-label="Voir le d&eacute;tail"
                      >
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      @if (demande.demande_status === 'PENDING' && permService.can('licence', 'demandes', 'validate')) {
                        <button
                          type="button"
                          class="w-8 h-8 rounded-xl flex items-center justify-center text-green-500 hover:bg-green-50 hover:text-green-700 transition-all duration-200 cursor-pointer"
                          (click)="validateDemande(demande)"
                          aria-label="Valider la demande"
                        >
                          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          class="w-8 h-8 rounded-xl flex items-center justify-center text-red-400 hover:bg-red-50 hover:text-red-600 transition-all duration-200 cursor-pointer"
                          (click)="rejectDemande(demande)"
                          aria-label="Rejeter la demande"
                        >
                          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      }
                    </td>
                  </tr>
                }

                @empty {
                  <tr>
                    <td colspan="9" class="px-6 py-16 text-center">
                      <div class="flex flex-col items-center gap-3">
                        <div class="w-14 h-14 rounded-3xl bg-stone-100 flex items-center justify-center">
                          <svg class="w-6 h-6 text-stone-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                          </svg>
                        </div>
                        <p class="text-stone-500 text-sm font-medium">Aucune demande trouv&eacute;e</p>
                        @if (searchQuery()) {
                          <p class="text-stone-400 text-xs">Essayez un autre terme de recherche</p>
                        }
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>

        @if (pagination().totalPages > 1) {
          <div class="flex items-center justify-between">
            <p class="text-xs text-stone-400">
              Page <span class="font-semibold text-stone-600">{{ pagination().page }}</span> sur <span class="font-semibold text-stone-600">{{ pagination().totalPages }}</span>
              &middot; <span class="font-semibold text-stone-600">{{ pagination().total }}</span> demande@if (pagination().total > 1) {s}
            </p>
            <div class="flex items-center gap-1">
              <button
                type="button"
                (click)="goToPage(pagination().page - 1)"
                [disabled]="pagination().page === 1 || isPageLoading()"
                class="w-9 h-9 rounded-xl border border-stone-200 bg-white text-stone-500 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center"
                aria-label="Page pr&eacute;c&eacute;dente"
              >
                <span class="material-icons" style="font-size: 18px;">chevron_left</span>
              </button>

              @for (p of pageNumbers(); track p) {
                @if (p === '...') {
                  <span class="w-9 h-9 flex items-center justify-center text-stone-400">&hellip;</span>
                } @else {
                  <button
                    type="button"
                    (click)="goToPage(p)"
                    [disabled]="isPageLoading()"
                    class="w-9 h-9 rounded-xl border transition-all flex items-center justify-center text-xs font-semibold"
                    [class.bg-primary-600]="pagination().page === p"
                    [class.text-white]="pagination().page === p"
                    [class.border-primary-600]="pagination().page === p"
                    [class.bg-white]="pagination().page !== p"
                    [class.text-stone-600]="pagination().page !== p"
                    [class.border-stone-200]="pagination().page !== p"
                    [class.hover:bg-stone-50]="pagination().page !== p"
                    [attr.aria-label]="'Aller &agrave; la page ' + p"
                  >
                    {{ p }}
                  </button>
                }
              }

              <button
                type="button"
                (click)="goToPage(pagination().page + 1)"
                [disabled]="pagination().page === pagination().totalPages || isPageLoading()"
                class="w-9 h-9 rounded-xl border border-stone-200 bg-white text-stone-500 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center"
                aria-label="Page suivante"
              >
                <span class="material-icons" style="font-size: 18px;">chevron_right</span>
              </button>
            </div>
          </div>
        }

      </div>
    </div>
  }

</div>
